<template>
  <article class="enablebanking-privacy">
    <h1>Privacy Notice</h1>

    <p>{{ $t('Last updated:') }} {{ new Date("2025-03-07").toLocaleDateString($i18n.locale, { year: 'numeric', month: 'long', day: 'numeric' }) }}</p>

    <p>This Privacy Notice explains how personal information is processed when you (“the Payment Service User” or “PSU”)
      use the Enable Banking API to share your payment account information held by Account Servicing Payment Service
      Providers (ASPSPs), which provides and maintains payment accounts for you, with third-party applications you are
      using.</p>

    <p>Enable Banking API is owned and operated by Enable Banking Oy, located in Otakaari 5, 02150 Espoo, Finland,
      company ID 2988499-7 (“Enable Banking”, “the company”, “our”, “we” or “us”).</p>

    <p>This notice is aligned with Enable Banking's Privacy and Personal Data Protection Policy which is built to
      enforce our commitment to data protection and compliance with applicable legislation relating to processing of
      personal data and data protection and especially the EU General Data Protection Regulation (EU 2016/679).</p>

    <h2>Nature and Purpose of Processing</h2>

    <p>The purpose of processing your personal data is to provide you with the capability to share your payment account
      information with applications using the Enable Banking API. </p>

    <p>The legal basis for processing your data is:</p>

    <ul>
      <li><a href="/terms">Terms of service</a>, which you (a PSU) have to accept prior
        to authorisation of access to payment account information you make at the ASPSP side.</li>
    </ul>

    <h2>Type of Personal Data and Categories of Data Subjects</h2>

    <p>Enable Banking processes the following categories of personal data when you use the Enable Banking API:</p>

    <ul>
      <li><strong>Payment account information</strong>: This information is shared with Enable Banking through ASPSPs'
        API after performance of strong customer authentication (SCA) and upon your explicit consent. The information
        may include payment account numbers, account holder names, names of the payment products and other properties of
        the payment accounts as well as payment account balances and payment transactions. </li>
      <li><strong>Credentials for access to payment account information:</strong> In case ASPSP&#39;s API requires PSU
        credentials for initiating authorisation of access to payment account information, Enable Banking API captures
        credentials from you, either directly or through the application, which your payment account information will be
        shared with. </li>
      <li><strong>Data necessary to persist authorisation sessions:</strong> This includes access tokens, consent
        identifiers, session validity periods and possibly other data necessary to retrieve payment account information
        and persist authorisation sessions. </li>
      <li><strong>User agent and IP address information</strong>: This includes data about your web browser or the
        application you used when requests for authorisation of access to payment account information or retrieval of
        payment account information are made on your behalf. This information is processed in order to fulfil
        requirements of ASPSPs' APIs.</li>
    </ul>

    <h2>Applicable Data Protection Measures</h2>

    <p>Enable Banking API does not store any personally identifiable information such as payment account numbers or
      account holder names.</p>

    <p>We implement appropriate technical and organisational measures to ensure the security of your personal data,
      including protection against unauthorised or unlawful processing and against accidental loss, destruction, or
      damage.</p>

    <p>Enable Banking accessed relevant risks and documented the measures it takes to mitigate the risks. Internally
      this is communicated through the Data Protection Impact Assessments.</p>

    <h2>Your Rights and Responsibilities</h2>

    <p>As a data subject, you have the following rights:</p>

    <ul>
      <li><strong>The right to be informed</strong>: You have the right to know what personal data we collect and how we
        use it. </li>
      <li><strong>The right of access</strong>: You have the right to access the personal data we hold about you. </li>
      <li><strong>The right to rectification</strong>: You have the right to correct any inaccurate or incomplete
        personal data we hold about you. </li>
      <li><strong>The right to erasure</strong>: You have the right to have your personal data deleted under certain
        circumstances. </li>
      <li><strong>The right to restrict processing</strong>: You have the right to restrict the processing of your
        personal data under certain circumstances. </li>
      <li><strong>The right to data portability</strong>: You have the right to receive your personal data in a
        structured, commonly used, and machine-readable format and to transmit it to another controller. </li>
      <li><strong>The right to object</strong>: You have the right to object to the processing of your personal data
        under certain circumstances.</li>
    </ul>

    <p>You must read and accept the Enable Banking API <a href="/terms">terms of
        service</a> before authenticating with an ASPSP and authorising Enable Banking to access your payment account
      information. Additionally, you should understand how the application you are using – and sharing your payment
      account information with through the Enable Banking API – will process your data, as Enable Banking does not
      control that.</p>

    <h2>Object and Duration of Data Processing</h2>

    <p>We use your personal information for the following purposes:</p>

    <ul>
      <li><strong>To enable data sharing</strong>: To facilitate the sharing of your payment account information with
        the applications you have authorised. </li>
      <li><strong>To maintain session persistence</strong>: To allow applications you have authorised to use your data
        to fetch you payment account information through Enable Banking API without your direct involvement. </li>
      <li><strong>To comply with legal obligations</strong>: To allow Enable Banking perform necessary financial
        monitoring to comply with applicable laws and regulations, including AML/CTF legislation. </li>
      <li><strong>To perform analytics:</strong> To collect statistics, which is used for maintenance of integrations
        towards ASPSPs and further development of Enable Banking API.</li>
    </ul>

    <p>We will retain your personal information for no longer than is necessary for the purposes for which it was
      collected:</p>

    <ul>
      <li>Payment account information is retained only for the time necessary to convert it from the format in which it
        is provided by ASPSPs to the format used by Enable Banking API, which is a maximum of 60 seconds. </li>
      <li>Credentials for access to payment account information are retained for the time necessary for you to complete
        authorisation of access to payment account information from an ASPSP, which is limited to 15 minutes. </li>
      <li>Data necessary to persist authorisation sessions is retained for the period requested by the application you
        are using, which is up to 180 days after authorisation of access to payment account information. </li>
      <li>User agent and IP address information is retained for the period of 30 days since it was either directly
        collected during the authorisation process or it was provided by the application you are using.</li>
    </ul>

    <h2>Sharing Your Personal Information</h2>

    <p>We may share your Personal Information with the following parties:</p>

    <ul>
      <li><strong>Applications you are using</strong>: To provide the data sharing service. The applications are either
        operated by customers of Enable Banking or by you. Enable Banking does not assess usage of your personal
        information by the applications. The <a href="/terms">terms of service</a>
        describe how you can review which applications your personal information is being shared with and terminate data
        sharing. </li>
      <li>
        <p><strong>Cloud service providers</strong>: To make Enable Banking API available to you, we use cloud service
          provided by the following companies: </p>
        <ul>
          <li>Google Cloud EMEA Limited<br>70 Sir John Rogerson's Quay, Dublin 2, Ireland </li>
          <li>Amazon Web Services EMEA SARL<br>Avenue John F. Kennedy 38, Luxembourg, 1855, Luxembourg</li>
        </ul>
        <p>Enable Banking carefully assesses cloud service providers and how they process your personal information. We
          make sure the cloud service providers process your data in the EEA.</p>
      </li>
      <li>
        <p><strong>Authorities</strong>: If required by law, we may disclose your information to comply with legal
          obligations.</p>
      </li>
    </ul>

    <h2>Contact Information</h2>

    <p>If you have any questions or concerns about this Privacy Notice or our data processing practices, please contact
      us at <a href="mailto:support@enablebanking.com">support@enablebanking.com</a>.</p>

    <h2>Changes</h2>

    <p>We may update this Privacy Notice from time to time to reflect changes to our data processing practices or legal
      obligations. We will post any changes on this webpage. As we do not have your contact information, we will not
      notify you directly. However, we will inform your application about changes made to this Privacy Notice, so they
      are able to communicate this to you.</p>
  </article>
</template>
